/**
 * Settings Manager Hook
 *
 * Provides a comprehensive interface for managing user settings
 * with validation, persistence, and real-time updates.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  settingsService,
  type UserSettings,
  type ValidationErrors,
} from '../services/settingsService';
import { useErrorHandler } from './useErrorHandler';

export interface SettingsManagerState {
  settings: UserSettings;
  isLoading: boolean;
  isSaving: boolean;
  isValid: boolean;
  errors: ValidationErrors;
  hasUnsavedChanges: boolean;
}

export interface SettingsManagerActions {
  updateSetting: <T>(path: string, value: T) => Promise<boolean>;
  updateSettings: (updates: Partial<UserSettings>) => Promise<boolean>;
  saveSetting: <T>(path: string, value: T) => Promise<boolean>;
  saveSettings: (updates?: Partial<UserSettings>) => Promise<boolean>;
  resetSettings: () => Promise<void>;
  revertToPersisted: () => void;
  exportSettings: () => string;
  importSettings: (settingsJson: string) => Promise<boolean>;
  clearErrors: () => void;
  validateSettings: () => ValidationErrors;
}

export const useSettingsManager = (): SettingsManagerState & SettingsManagerActions => {
  const [state, setState] = useState<SettingsManagerState>({
    settings: settingsService.getSettings(),
    isLoading: false,
    isSaving: false,
    isValid: settingsService.isValid(),
    errors: {},
    hasUnsavedChanges: false,
  });

  const { handleComponentError } = useErrorHandler('SettingsManager');

  // Subscribe to settings changes
  useEffect(() => {
    const unsubscribe = settingsService.subscribe((newSettings) => {
      setState((prev) => ({
        ...prev,
        settings: newSettings,
        isValid: settingsService.isValid(),
      }));
    });

    return unsubscribe;
  }, []);

  // Update a single setting (in memory only)
  const updateSetting = useCallback(
    async <T>(path: string, value: T): Promise<boolean> => {
      try {
        setState((prev) => ({ ...prev, isLoading: true, errors: {} }));

        const result = await settingsService.updateSetting(path, value, {
          validate: true,
          persist: false, // Don't persist immediately
        });

        if (result.success) {
          setState((prev) => ({
            ...prev,
            hasUnsavedChanges: true,
            isValid: settingsService.isValid(),
            isLoading: false,
          }));
          return true;
        }
        setState((prev) => ({
          ...prev,
          errors: result.errors || {},
          isLoading: false,
        }));
        return false;
      } catch (error) {
        handleComponentError(error as Error, 'update_setting', {
          additionalData: { path, value },
        });
        setState((prev) => ({
          ...prev,
          errors: { [path]: 'Failed to update setting' },
          isLoading: false,
        }));
        return false;
      }
    },
    [handleComponentError],
  );

  // Update multiple settings (in memory only)
  const updateSettings = useCallback(
    async (updates: Partial<UserSettings>): Promise<boolean> => {
      try {
        setState((prev) => ({ ...prev, isLoading: true, errors: {} }));

        const result = await settingsService.updateSettings(updates, {
          validate: true,
          persist: false,
        });

        if (result.success) {
          setState((prev) => ({
            ...prev,
            hasUnsavedChanges: true,
            isValid: settingsService.isValid(),
            isLoading: false,
          }));
          return true;
        }
        setState((prev) => ({
          ...prev,
          errors: result.errors || {},
          isLoading: false,
        }));
        return false;
      } catch (error) {
        handleComponentError(error as Error, 'update_settings', {
          additionalData: { updates },
        });
        setState((prev) => ({
          ...prev,
          errors: { general: 'Failed to update settings' },
          isLoading: false,
        }));
        return false;
      }
    },
    [handleComponentError],
  );

  // Save a single setting (persist immediately)
  const saveSetting = useCallback(
    async <T>(path: string, value: T): Promise<boolean> => {
      try {
        setState((prev) => ({ ...prev, isSaving: true, errors: {} }));

        const result = await settingsService.updateSetting(path, value, {
          validate: true,
          persist: true,
        });

        if (result.success) {
          setState((prev) => ({
            ...prev,
            hasUnsavedChanges: false,
            isValid: settingsService.isValid(),
            isSaving: false,
          }));
          return true;
        }
        setState((prev) => ({
          ...prev,
          errors: result.errors || {},
          isSaving: false,
        }));
        return false;
      } catch (error) {
        handleComponentError(error as Error, 'save_setting', {
          additionalData: { path, value },
        });
        setState((prev) => ({
          ...prev,
          errors: { [path]: 'Failed to save setting' },
          isSaving: false,
        }));
        return false;
      }
    },
    [handleComponentError],
  );

  // Save all settings (persist current state)
  const saveSettings = useCallback(
    async (updates?: Partial<UserSettings>): Promise<boolean> => {
      try {
        setState((prev) => ({ ...prev, isSaving: true, errors: {} }));

        let result: { success: boolean; errors?: ValidationErrors };
        if (updates) {
          // Save with updates
          result = await settingsService.updateSettings(updates, {
            validate: true,
            persist: true,
          });
        } else {
          // Save what updateSetting staged in memory. An empty updates object
          // would name no sections and persist only metadata (SM-1268).
          result = await settingsService.flushPendingChanges();
        }

        if (result.success) {
          setState((prev) => ({
            ...prev,
            hasUnsavedChanges: false,
            isValid: settingsService.isValid(),
            isSaving: false,
          }));
          return true;
        }
        setState((prev) => ({
          ...prev,
          errors: result.errors || {},
          isSaving: false,
        }));
        return false;
      } catch (error) {
        handleComponentError(error as Error, 'save_settings', {
          additionalData: { updates },
        });
        setState((prev) => ({
          ...prev,
          errors: { general: 'Failed to save settings' },
          isSaving: false,
        }));
        return false;
      }
    },
    [handleComponentError],
  );

  // Reset settings to defaults
  const resetSettings = useCallback(async (): Promise<void> => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, errors: {} }));
      await settingsService.resetToDefaults();
      setState((prev) => ({
        ...prev,
        hasUnsavedChanges: false,
        isValid: true,
        isLoading: false,
      }));
    } catch (error) {
      handleComponentError(error as Error, 'reset_settings');
      setState((prev) => ({
        ...prev,
        errors: { general: 'Failed to reset settings' },
        isLoading: false,
      }));
    }
  }, [handleComponentError]);

  // Revert in-memory settings to persisted state (e.g. when user cancels modal)
  const revertToPersisted = useCallback((): void => {
    settingsService.revertToPersisted();
    setState((prev) => ({
      ...prev,
      settings: settingsService.getSettings(),
      hasUnsavedChanges: false,
      isValid: settingsService.isValid(),
    }));
  }, []);

  // Export settings
  const exportSettings = useCallback((): string => {
    try {
      return settingsService.exportSettings();
    } catch (error) {
      handleComponentError(error as Error, 'export_settings');
      return '{}';
    }
  }, [handleComponentError]);

  // Import settings
  const importSettings = useCallback(
    async (settingsJson: string): Promise<boolean> => {
      try {
        setState((prev) => ({ ...prev, isLoading: true, errors: {} }));
        const result = await settingsService.importSettings(settingsJson);

        if (result.success) {
          setState((prev) => ({
            ...prev,
            hasUnsavedChanges: false,
            isValid: settingsService.isValid(),
            isLoading: false,
          }));
          return true;
        }
        setState((prev) => ({
          ...prev,
          errors: result.errors || {},
          isLoading: false,
        }));
        return false;
      } catch (error) {
        handleComponentError(error as Error, 'import_settings');
        setState((prev) => ({
          ...prev,
          errors: { general: 'Failed to import settings' },
          isLoading: false,
        }));
        return false;
      }
    },
    [handleComponentError],
  );

  // Clear validation errors
  const clearErrors = useCallback((): void => {
    setState((prev) => ({ ...prev, errors: {} }));
  }, []);

  // Validate current settings
  const validateSettings = useCallback((): ValidationErrors => {
    return settingsService.getCurrentValidationErrors();
  }, []);

  return {
    // State
    ...state,

    // Actions
    updateSetting,
    updateSettings,
    saveSetting,
    saveSettings,
    resetSettings,
    revertToPersisted,
    exportSettings,
    importSettings,
    clearErrors,
    validateSettings,
  };
};

export default useSettingsManager;
